"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { TOUR_STEPS, MOBILE_TOUR_STEPS, type TourStep } from "./tour-steps";
import { useIsMobile } from "@/hooks/use-mobile";
import { TourTooltip } from "./tour-tooltip";
import { WelcomeModal } from "./welcome-modal";
import { TourFeedbackModal } from "./lazy-tour-feedback-modal";
import { safeGet, safeSet, safeRemove } from "./safe-storage";
import { useRect } from "./use-rect";
import { markTourCompleted } from "@/lib/actions/tour";
import { trackTourEvent } from "@/lib/actions/tour-analytics";
import {
  incrementTourSkip,
  resetTourNagState,
  shouldShowWelcomeModal,
} from "./tour-nagging";

const getStorageKey = (userId?: string) =>
  userId ? `upsider_tour_completed:${userId}` : "upsider_tour_completed";
const TARGET_RETRY_ATTEMPTS = 20;
const TARGET_RETRY_INTERVAL = 150;

function findVisibleTarget(selector: string): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(selector)) {
    if (el.getClientRects().length > 0) return el;
  }
  return null;
}

interface TourContextValue {
  isRunning: boolean;
  isWelcomeOpen: boolean;
  currentStep: number;
  startTour: () => void;
  restartTour: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within TourProvider");
  }
  return context;
}

interface TourProviderProps {
  children: ReactNode;
  startDelay?: number;
  initialTourCompleted?: boolean;
  userId?: string;
  shouldRestart?: boolean;
  autoShowWelcome?: boolean;
}

export function TourProvider({
  children,
  startDelay = 1500,
  initialTourCompleted = false,
  userId,
  shouldRestart = false,
  autoShowWelcome = true,
}: TourProviderProps) {
  const storageKey = getStorageKey(userId);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [target, setTarget] = useState<{ step: TourStep; element: HTMLElement } | null>(null);
  const [mounted, setMounted] = useState(false);

  const isMobile = useIsMobile();
  const pathname = usePathname();
  const [lockedIsMobile, setLockedIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    if (isRunning && lockedIsMobile === null) {
      setLockedIsMobile(isMobile);
    }
    if (!isRunning) {
      setLockedIsMobile(null);
    }
  }, [isRunning, isMobile, lockedIsMobile]);

  const activeSteps = (lockedIsMobile ?? isMobile) ? MOBILE_TOUR_STEPS : TOUR_STEPS;
  const stepSetLabel = (lockedIsMobile ?? isMobile) ? "mobile" : "desktop";
  const targetElement = target?.step === activeSteps[currentStep] ? target.element : null;

  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const welcomeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sessionIdRef = useRef<string>("");
  const tourStartTimeRef = useRef<number>(0);
  const stepStartTimeRef = useRef<number>(0);
  const lastViewedStepRef = useRef<number>(-1);

  const clearWelcomeTimer = useCallback(() => {
    if (welcomeTimerRef.current) {
      clearTimeout(welcomeTimerRef.current);
      welcomeTimerRef.current = null;
    }
  }, []);

  const ensureSessionId = useCallback(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = crypto.randomUUID();
    }
    return sessionIdRef.current;
  }, []);

  const initTourTiming = useCallback(() => {
    tourStartTimeRef.current = Date.now();
    stepStartTimeRef.current = Date.now();
  }, []);

  const targetRect = useRect(targetElement);

  const markComplete = useCallback(() => {
    const localSaved = safeSet(storageKey, "true");
    markTourCompleted()
      .then((res) => {
        if (!res.success && !localSaved) {
          console.error("Tour completion failed to persist (both localStorage and server):", res.error);
        } else if (!res.success) {
          console.warn("Tour completion not persisted to server (localStorage ok):", res.error);
        }
      })
      .catch((err) => {
        if (!localSaved) {
          console.error("Tour completion failed to persist (both localStorage and server):", err);
        } else {
          console.error("Failed to persist tour completion to server:", err);
        }
      });
  }, [storageKey]);

  const handleFinish = useCallback(() => {
    clearWelcomeTimer();
    const sessionId = ensureSessionId();

    const stepDuration = Date.now() - stepStartTimeRef.current;
    void trackTourEvent(sessionId, "step_completed", {
      step_index: currentStep,
      step_id: activeSteps[currentStep]?.id,
      duration_ms: stepDuration,
      step_set: stepSetLabel,
    });

    const totalDuration = Date.now() - tourStartTimeRef.current;
    void trackTourEvent(sessionId, "tour_completed", {
      total_duration_ms: totalDuration,
      step_set: stepSetLabel,
    });
    setIsRunning(false);
    setCurrentStep(0);
    markComplete();
    resetTourNagState(userId);
    setShowFeedback(true);
  }, [markComplete, clearWelcomeTimer, ensureSessionId, currentStep, userId, activeSteps, stepSetLabel]);

  const handleSkip = useCallback(() => {
    clearWelcomeTimer();
    const sessionId = ensureSessionId();

    if (showWelcome) {
      void trackTourEvent(sessionId, "tour_skipped_welcome");
    } else if (isRunning) {
      const duration = Date.now() - stepStartTimeRef.current;
      void trackTourEvent(sessionId, "tour_skipped", {
        step_index: currentStep,
        step_id: activeSteps[currentStep]?.id,
        duration_ms: duration,
        step_set: stepSetLabel,
      });
    }
    setShowWelcome(false);
    setIsRunning(false);
    setCurrentStep(0);
    incrementTourSkip(userId);
    setTimeout(() => lastFocusedRef.current?.focus(), 0);
  }, [clearWelcomeTimer, ensureSessionId, showWelcome, isRunning, currentStep, userId, activeSteps, stepSetLabel]);

  useEffect(() => {
    if (isRunning && pathname !== "/optimizer") handleSkip();
  }, [isRunning, pathname, handleSkip]);

  const handleStartTour = useCallback(() => {
    clearWelcomeTimer();

    const sessionId = ensureSessionId();
    initTourTiming();
    void trackTourEvent(sessionId, "tour_started", {
      step_set: isMobile ? "mobile" : "desktop",
    });

    setShowWelcome(false);
    setCurrentStep(0);
    setIsRunning(true);
  }, [clearWelcomeTimer, ensureSessionId, initTourTiming, isMobile]);

  const handleSurveyComplete = useCallback(
    (_prefs: { sportsbooks: string[]; sports: string[] }) => {
    },
    [],
  );

  const handleNext = useCallback(() => {
    if (currentStep < activeSteps.length - 1) {
      const duration = Date.now() - stepStartTimeRef.current;
      void trackTourEvent(ensureSessionId(), "step_completed", {
        step_index: currentStep,
        step_id: activeSteps[currentStep]?.id,
        duration_ms: duration,
        step_set: stepSetLabel,
      });
      stepStartTimeRef.current = Date.now();
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, ensureSessionId, activeSteps, stepSetLabel]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      const duration = Date.now() - stepStartTimeRef.current;
      void trackTourEvent(ensureSessionId(), "step_completed", {
        step_index: currentStep,
        step_id: activeSteps[currentStep]?.id,
        duration_ms: duration,
        step_set: stepSetLabel,
      });
      stepStartTimeRef.current = Date.now();
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep, ensureSessionId, activeSteps, stepSetLabel]);

  const restartTour = useCallback(() => {
    clearWelcomeTimer();
    safeRemove(storageKey);
    setIsRunning(false);
    setCurrentStep(0);
    lastFocusedRef.current = document.activeElement as HTMLElement;
    sessionIdRef.current = crypto.randomUUID();
    setShowWelcome(true);
  }, [storageKey, clearWelcomeTimer]);

  const startTour = useCallback(() => {
    clearWelcomeTimer();
    lastFocusedRef.current = document.activeElement as HTMLElement;

    sessionIdRef.current = crypto.randomUUID();
    initTourTiming();
    void trackTourEvent(sessionIdRef.current, "tour_started", {
      step_set: isMobile ? "mobile" : "desktop",
    });

    setShowWelcome(false);
    setCurrentStep(0);
    setIsRunning(true);
  }, [clearWelcomeTimer, initTourTiming, isMobile]);

  useEffect(() => {
    setMounted(true);

    if (initialTourCompleted) {
      safeSet(storageKey, "true");
      return;
    }

    const hasCompletedLocally = safeGet(storageKey) === "true";
    if (hasCompletedLocally) return;

    if (!autoShowWelcome) {
      return;
    }

    if (!shouldShowWelcomeModal(userId)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      lastFocusedRef.current = document.activeElement as HTMLElement;
      sessionIdRef.current = crypto.randomUUID();
      setShowWelcome(true);
    }, startDelay);
    welcomeTimerRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      if (welcomeTimerRef.current === timeoutId) {
        welcomeTimerRef.current = null;
      }
    };
  }, [startDelay, initialTourCompleted, storageKey, clearWelcomeTimer, autoShowWelcome, userId]);

  useEffect(() => {
    if (shouldRestart && mounted) {
      const url = new URL(window.location.href);
      url.searchParams.delete("restart_tour");
      window.history.replaceState({}, "", url.pathname + url.search);

      clearWelcomeTimer();
      safeRemove(storageKey);
      setIsRunning(false);
      setCurrentStep(0);
      lastFocusedRef.current = document.activeElement as HTMLElement;
      sessionIdRef.current = crypto.randomUUID();
      setShowWelcome(true);
    }
  }, [shouldRestart, mounted, storageKey, clearWelcomeTimer]);

  useEffect(() => {
    if (showWelcome && sessionIdRef.current) {
      void trackTourEvent(sessionIdRef.current, "welcome_shown");
    }
  }, [showWelcome]);

  useEffect(() => {
    if (isRunning && targetElement && targetRect) {
      if (currentStep !== lastViewedStepRef.current) {
        const step = activeSteps[currentStep];
        if (step && sessionIdRef.current) {
          lastViewedStepRef.current = currentStep;
          void trackTourEvent(sessionIdRef.current, "step_viewed", {
            step_index: currentStep,
            step_id: step.id,
            step_title: step.title,
            step_set: stepSetLabel,
          });
        }
      }
    }
  }, [isRunning, currentStep, targetElement, targetRect, activeSteps, stepSetLabel]);

  useEffect(() => {
    if (!isRunning) {
      lastViewedStepRef.current = -1;
    }
  }, [isRunning]);

  useEffect(() => {
    if (!isRunning) {
      setTarget(null);
      return;
    }

    const step = activeSteps[currentStep];
    if (!step) return;

    let attempts = 0;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const tryFindTarget = () => {
      const el = findVisibleTarget(step.target);
      if (el) {
        if (intervalId) clearInterval(intervalId);
        setTarget({ step, element: el });
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      attempts++;
      if (attempts >= TARGET_RETRY_ATTEMPTS) {
        if (intervalId) clearInterval(intervalId);
        console.warn(`Tour target not found after ${attempts} attempts: ${step.target}`);
        void trackTourEvent(sessionIdRef.current, "step_skipped_not_found", {
          step_index: currentStep,
          step_id: step.id,
          selector: step.target,
          step_set: stepSetLabel,
        });
        if (currentStep < activeSteps.length - 1) {
          stepStartTimeRef.current = Date.now();
          setCurrentStep((prev) => prev + 1);
        } else {
          handleFinish();
        }
      }
    };

    intervalId = setInterval(tryFindTarget, TARGET_RETRY_INTERVAL);
    tryFindTarget();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isRunning, currentStep, handleFinish, activeSteps, stepSetLabel]);

  useEffect(() => {
    if (!isRunning || !targetElement || targetRect || document.body.contains(targetElement)) return;

    const step = activeSteps[currentStep];
    if (!step) return;

    let attempts = 0;
    const maxAttempts = 10;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const tryRecoverTarget = () => {
      const el = findVisibleTarget(step.target);
      if (el) {
        if (intervalId) clearInterval(intervalId);
        setTarget({ step, element: el });
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      attempts++;
      if (attempts >= maxAttempts) {
        if (intervalId) clearInterval(intervalId);
        console.warn(`Tour target disappeared and could not recover: ${step.target}`);
        void trackTourEvent(sessionIdRef.current, "step_skipped_not_found", {
          step_index: currentStep,
          step_id: step.id,
          selector: step.target,
          step_set: stepSetLabel,
        });
        if (currentStep < activeSteps.length - 1) {
          stepStartTimeRef.current = Date.now();
          setCurrentStep((prev) => prev + 1);
        } else {
          handleFinish();
        }
      }
    };

    intervalId = setInterval(tryRecoverTarget, TARGET_RETRY_INTERVAL);
    tryRecoverTarget();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isRunning, targetElement, targetRect, currentStep, handleFinish, activeSteps, stepSetLabel]);

  useEffect(() => {
    if (!isRunning) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isEditable =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable;
      const hasModifier = e.metaKey || e.ctrlKey || e.altKey;

      if (isEditable || hasModifier) {
        if (e.key === "Escape") {
          handleSkip();
        }
        return;
      }

      if (e.key === "Escape") {
        handleSkip();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (currentStep < activeSteps.length - 1) {
          handleNext();
        } else {
          handleFinish();
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isRunning, currentStep, handleSkip, handleFinish, handleNext, handlePrev, activeSteps]);

  const handleFeedbackClose = useCallback(() => {
    setShowFeedback(false);
    setTimeout(() => lastFocusedRef.current?.focus(), 0);
  }, []);

  const contextValue: TourContextValue = {
    isRunning,
    isWelcomeOpen: showWelcome,
    currentStep,
    startTour,
    restartTour,
  };

  return (
    <TourContext.Provider value={contextValue}>
      {children}

      <WelcomeModal
        open={showWelcome}
        onStartTour={handleStartTour}
        onSkip={handleSkip}
        onSurveyComplete={handleSurveyComplete}
      />

      <TourFeedbackModal
        open={showFeedback}
        sessionId={sessionIdRef.current}
        onClose={handleFeedbackClose}
      />

      {mounted &&
        isRunning &&
        createPortal(
          <>
            <TourSpotlight rect={targetRect} />

            {targetRect && (
              <TourTooltip
                step={activeSteps[currentStep]}
                stepIndex={currentStep}
                totalSteps={activeSteps.length}
                onNext={handleNext}
                onPrev={handlePrev}
                onSkip={handleSkip}
                onFinish={handleFinish}
                targetRect={targetRect}
              />
            )}
          </>,
          document.body
        )}
    </TourContext.Provider>
  );
}

export function TourSpotlight({ rect }: { rect: DOMRect | null }) {
  const padding = 8;

  if (!rect) return <div className="fixed inset-0 z-[9998] pointer-events-none bg-black/70" />;

  return (
    <div className="fixed inset-0 z-[9998] pointer-events-auto">
      <div
        className="absolute rounded-lg"
        style={{
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
          boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 0 2px hsl(var(--primary))",
        }}
      />
    </div>
  );
}
